import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { type EmployeeProfile } from '../../../shared/api/employee.service';
import { zIndexMap } from '../../../shared/config';
import {
  PersonalDataInputField,
  PrimaryButton,
  TaxIdHelpSheet,
} from '../../../shared/ui';
import { useMyPageSettingsPersonalDataForm } from '../model/use-my-page-settings-personal-data-form';

type MyPageSettingsPersonalDataScreenProps = {
  initialProfile?: EmployeeProfile | null;
  onComplete?: () => void;
};

const Screen = styled.section.attrs({
  'data-ui': 'my-page-settings-personal-data-screen',
})`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(22)};
  padding-top: ${({ theme }) => theme.scale.px(14)};
`;

const Fields = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(18)};
`;

const SubmitError = styled.p`
  margin: 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.support.danger};
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(14)};
`;

const SubmitButton = styled(PrimaryButton)`
  justify-self: center;
`;

export function MyPageSettingsPersonalDataScreen({
  initialProfile,
  onComplete,
}: MyPageSettingsPersonalDataScreenProps) {
  const { t } = useTranslation();
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const form = useMyPageSettingsPersonalDataForm({
    initialProfile,
  });

  return (
    <Screen>
      <Fields>
        <PersonalDataInputField
          ariaLabel={t('myPageSettings.profile.emailLabel')}
          dataUi="my-page-settings-personal-data-email"
          isInvalid={false}
          label={t('myPageSettings.profile.emailLabel')}
          placeholder={t('myPageSettings.profile.emailPlaceholder')}
          readOnly
          value={initialProfile?.email ?? ''}
        />

        <PersonalDataInputField
          ariaLabel={t('registrationIdentityPersonalData.firstNameLabel')}
          dataUi="my-page-settings-personal-data-first-name"
          isInvalid={false}
          label={t('registrationIdentityPersonalData.firstNameLabel')}
          placeholder={t(
            'registrationIdentityPersonalData.firstNamePlaceholder',
          )}
          value={form.values.firstName}
          onValueChange={nextValue => {
            form.setFieldValue('firstName', nextValue);
          }}
        />

        <PersonalDataInputField
          ariaLabel={t('registrationIdentityPersonalData.lastNameLabel')}
          dataUi="my-page-settings-personal-data-last-name"
          isInvalid={false}
          label={t('registrationIdentityPersonalData.lastNameLabel')}
          placeholder={t(
            'registrationIdentityPersonalData.lastNamePlaceholder',
          )}
          value={form.values.lastName}
          onValueChange={nextValue => {
            form.setFieldValue('lastName', nextValue);
          }}
        />

        <PersonalDataInputField
          ariaLabel={t(form.taxIdField.labelKey)}
          dataUi="my-page-settings-personal-data-tax-id"
          disabled={form.isTaxIdLocked}
          inputMode="numeric"
          invalidMessage={form.taxIdErrorMessage ?? undefined}
          isInvalid={form.isTaxIdInvalid}
          label={t(form.taxIdField.labelKey)}
          mask={form.taxIdField.mask}
          placeholder={form.taxIdField.placeholder}
          showHelpTip={form.taxIdField.showHelpTip}
          tipAriaLabel={
            form.taxIdField.tipAriaLabelKey
              ? t(form.taxIdField.tipAriaLabelKey)
              : undefined
          }
          value={form.values.taxId}
          onOpenHelp={() => {
            setIsHelpOpen(true);
          }}
          onValueChange={nextValue => {
            form.setFieldValue('taxId', nextValue);
          }}
        />
      </Fields>

      {form.submitError ? <SubmitError>{form.submitError}</SubmitError> : null}

      <SubmitButton
        type="button"
        disabled={!form.canSubmit || form.isSubmitting}
        onClick={async () => {
          const isSuccessful = await form.submit();

          if (isSuccessful) {
            onComplete?.();
          }
        }}
      >
        {t('myPageSettings.profile.continue')}
      </SubmitButton>

      <TaxIdHelpSheet
        dataUi="my-page-settings-tax-id-help-sheet"
        isOpen={isHelpOpen}
        onClose={() => {
          setIsHelpOpen(false);
        }}
        taxIdField={form.taxIdField}
        zIndex={zIndexMap.myPageSettings.taxIdHelpOverlay}
      />
    </Screen>
  );
}
